import { BMPrimitives } from "@traveloka/district-bloom";
import { useCallback, useEffect, useRef, useState } from "react";
import { Animated, Easing } from "react-native";

export const MAX_HEIGHT_OPTIONS_CONTAINER = 250;

export function useGetValue<T>(getOptionValue?: (value: T) => string) {
  return useCallback(
    (value?: T | null): string => {
      if (value == null) return "";
      if (typeof value === "string") return value;
      if (!getOptionValue) {
        console.error(
          "[BMDropdownDesktopOptions] Missing getOptionValue for non-string value",
          { value }
        );
        return "";
      }
      return getOptionValue(value);
    },
    [getOptionValue]
  );
}

export function useAnimatedDropdownHeight(
  show: boolean,
  contentHeight: number,
  maxHeight = MAX_HEIGHT_OPTIONS_CONTAINER,
  offset = 10
) {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const height = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const { BMAnimation } = BMPrimitives;
    const transition = show
      ? BMAnimation.transition.easeOutBounce
      : BMAnimation.transition.easeInOut;

    Animated.timing(height, {
      toValue: show ? Math.min(contentHeight, maxHeight) : 0,
      duration: BMAnimation.duration.moderate,
      easing: Easing.bezier(
        transition.x1,
        transition.y1,
        transition.x2,
        transition.y2
      ),
      useNativeDriver: false,
    }).start();
  }, [show, contentHeight, height, maxHeight]);

  useEffect(() => {
    const id = height.addListener(({ value }) => {
      const collapsed = offset > value;
      setIsCollapsed((prev) => (prev === collapsed ? prev : collapsed));
    });
    return () => height.removeListener(id);
  }, [height, offset]);

  return { height, isCollapsed };
}
